/**
 * HandoverCard — what the incoming nurse sees when a patient arrived via a
 * handover: who handed the patient over, when, and the Rapport session the
 * record lives in. The card is a POINTER — the note, the dialogue and the
 * flagged conversations are messages in that session; nothing is duplicated
 * here.
 *
 * Rendered at the top of the patient's conversation list (Home pane 2) while
 * the assignment that carried it is the nurse's current one.
 */

import { useTranslation } from 'react-i18next';
import { ArrowRightLeft, ChevronRight } from 'lucide-react';
import { useDateFnsLocale } from '@/hooks/useDateFnsLocale';
import { safeFormatDistanceToNow } from '@/utils/dates';
import type { AssignmentHandover } from '@/api/endpoints/shifts';

interface HandoverCardProps {
  handover: AssignmentHandover;
  /** When the handover completed = the incoming assignment's started_at. */
  receivedAt: string;
  /** Opens the Rapport session (select in pane 3 / navigate). */
  onOpenRapport: (sessionId: string) => void;
}

export function HandoverCard({ handover, receivedAt, onOpenRapport }: HandoverCardProps) {
  const { t } = useTranslation();
  const dateLocale = useDateFnsLocale();

  // A custody-only transfer carries no record — nothing to show.
  if (!handover.handover_session_id) return null;
  const rapportSessionId = handover.handover_session_id;

  return (
    <section
      aria-label={t('home.handover.receivedUnknown', { defaultValue: 'Handover' })}
      className="overflow-hidden rounded-xl border border-primary-100 bg-white"
    >
      <div className="flex">
        <div className="w-1 shrink-0 bg-primary" aria-hidden="true" />
        <div className="min-w-0 flex-1 p-3.5">
          <div className="flex items-center justify-between gap-2">
            <span className="flex min-w-0 items-center gap-2">
              <span className="grid h-6 w-6 flex-none place-items-center rounded-md bg-primary-50 text-primary-700">
                <ArrowRightLeft className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
              <span className="truncate text-[13px] font-bold text-neutral-900">
                {handover.from_nurse_name
                  ? t('home.handover.receivedFrom', {
                      name: handover.from_nurse_name,
                      defaultValue: 'Handover from {{name}}',
                    })
                  : t('home.handover.receivedUnknown', { defaultValue: 'Handover' })}
              </span>
            </span>
            <span className="flex-none text-[12px] text-neutral-400">
              {safeFormatDistanceToNow(
                receivedAt,
                { addSuffix: true, locale: dateLocale },
                '–',
              )}
            </span>
          </div>

          <button
            type="button"
            onClick={() => onOpenRapport(rapportSessionId)}
            className="mt-2 flex w-full min-h-[44px] items-center justify-between gap-2 rounded-lg bg-primary-50/60 px-3 py-2 text-left transition-colors hover:bg-primary-50 active:scale-[0.99]"
          >
            <span className="text-[13px] font-semibold text-primary-700">
              {t('home.handover.viewRapport', {
                defaultValue: 'View the handover Rapport',
              })}
            </span>
            <ChevronRight className="h-4 w-4 flex-none text-primary-700" aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
}
